import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import * as React from "react";
import { useNavigate } from "react-router-dom";

const EQUIPMENT_TYPES = [
  { value: "van", label: "Van / Dry Van" },
  { value: "flatbed", label: "Flatbed" },
  { value: "reefer", label: "Reefer" },
  { value: "step_deck", label: "Step Deck" },
  { value: "box_truck", label: "Box Truck" },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
    />
  );
}

function Select(props: React.SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string; label: string }[] }) {
  const { options, ...rest } = props;
  return (
    <select
      {...rest}
      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export default function NewLoadPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [form, setForm] = React.useState({
    origin_name: "",
    origin_address: "",
    origin_city: "",
    origin_state: "",
    origin_zip: "",
    pickup_window_start: "",
    pickup_window_end: "",
    dest_name: "",
    dest_address: "",
    dest_city: "",
    dest_state: "",
    dest_zip: "",
    delivery_window_start: "",
    delivery_window_end: "",
    equipment_type: "van",
    weight_lbs: "",
    commodity: "",
    special_instructions: "",
    customer_name: "",
    customer_ref: "",
  });

  function set(field: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = { ...form };
      if (!body.pickup_window_start) delete body.pickup_window_start;
      if (!body.pickup_window_end) delete body.pickup_window_end;
      if (!body.delivery_window_start) delete body.delivery_window_start;
      if (!body.delivery_window_end) delete body.delivery_window_end;
      if (!body.weight_lbs) delete body.weight_lbs;

      const res = await fetch("/api/tms/dispatch/loads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = (await res.json()) as { status: string; data?: { id: string }; error?: string };
      if (json.status === "success" && json.data) {
        navigate(`/dispatch/loads/${json.data.id}`);
      } else {
        setError(json.error ?? "Failed to create load.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dispatch")}>← Back</Button>
        <h1 className="text-xl font-semibold">New Load</h1>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        {/* Origin */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Origin</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Facility Name">
              <Input placeholder="Warehouse A" value={form.origin_name} onChange={(e) => set("origin_name", e.target.value)} />
            </Field>
            <Field label="Address *">
              <Input required placeholder="123 Industrial Dr" value={form.origin_address} onChange={(e) => set("origin_address", e.target.value)} />
            </Field>
            <Field label="City *">
              <Input required placeholder="Chicago" value={form.origin_city} onChange={(e) => set("origin_city", e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="State *">
                <Input required placeholder="IL" maxLength={5} value={form.origin_state} onChange={(e) => set("origin_state", e.target.value)} />
              </Field>
              <Field label="ZIP *">
                <Input required placeholder="60601" value={form.origin_zip} onChange={(e) => set("origin_zip", e.target.value)} />
              </Field>
            </div>
            <Field label="Pickup Window Start">
              <DateTimePicker value={form.pickup_window_start} onChange={(value) => set("pickup_window_start", value)} />
            </Field>
            <Field label="Pickup Window End">
              <DateTimePicker value={form.pickup_window_end} onChange={(value) => set("pickup_window_end", value)} />
            </Field>
          </CardContent>
        </Card>

        {/* Destination */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Destination</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Facility Name">
              <Input placeholder="Distribution Center" value={form.dest_name} onChange={(e) => set("dest_name", e.target.value)} />
            </Field>
            <Field label="Address *">
              <Input required placeholder="456 Commerce Blvd" value={form.dest_address} onChange={(e) => set("dest_address", e.target.value)} />
            </Field>
            <Field label="City *">
              <Input required placeholder="Dallas" value={form.dest_city} onChange={(e) => set("dest_city", e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="State *">
                <Input required placeholder="TX" maxLength={5} value={form.dest_state} onChange={(e) => set("dest_state", e.target.value)} />
              </Field>
              <Field label="ZIP *">
                <Input required placeholder="75201" value={form.dest_zip} onChange={(e) => set("dest_zip", e.target.value)} />
              </Field>
            </div>
            <Field label="Delivery Window Start">
              <DateTimePicker value={form.delivery_window_start} onChange={(value) => set("delivery_window_start", value)} />
            </Field>
            <Field label="Delivery Window End">
              <DateTimePicker value={form.delivery_window_end} onChange={(value) => set("delivery_window_end", value)} />
            </Field>
          </CardContent>
        </Card>

        {/* Cargo */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Cargo</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Equipment Type *">
              <Select
                options={EQUIPMENT_TYPES}
                value={form.equipment_type}
                onChange={(e) => set("equipment_type", e.target.value)}
              />
            </Field>
            <Field label="Weight (lbs)">
              <Input type="number" placeholder="42000" value={form.weight_lbs} onChange={(e) => set("weight_lbs", e.target.value)} />
            </Field>
            <Field label="Commodity">
              <Input placeholder="General Freight" value={form.commodity} onChange={(e) => set("commodity", e.target.value)} />
            </Field>
            <Field label="Special Instructions">
              <Input placeholder="Liftgate required" value={form.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} />
            </Field>
          </CardContent>
        </Card>

        {/* Customer */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Customer</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Customer Name">
              <Input placeholder="Acme Corp" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
            </Field>
            <Field label="Customer Reference">
              <Input placeholder="PO-12345" value={form.customer_ref} onChange={(e) => set("customer_ref", e.target.value)} />
            </Field>
          </CardContent>
        </Card>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate("/dispatch")}>Cancel</Button>
          <Button type="submit" disabled={submitting}>{submitting ? "Creating..." : "Create Load"}</Button>
        </div>
      </form>
    </div>
  );
}
